import { useCallback, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { Product, ProductTrackingType } from '../../../../clients/quipli-service/product/product';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../util/toasts';

export interface UseProductItemAvailabilityProps {
  isRental: boolean;
  isCustomProduct: boolean;
  isNewProduct: boolean;
  productId: number;
  quantity: number;
  initialStock: {
    rentalStock?: number;
    saleStock?: number;
  };
  trackingType?: ProductTrackingType;
  startDate?: DateTime;
  endDate?: DateTime;
  locationId: number;
  orderId?: number;
  name: string;
  fetchedProduct?: Product;
}

export interface UseProductItemAvailabilityReturn {
  qtyAvailable?: number;
  showQtyWarning: boolean;
  showOverbookWarning: boolean;
  checkAvailableQuantity: (newQty: number, schedule?: { start: DateTime; end?: DateTime }) => void;
}

export const useProductItemAvailability = ({
  isRental,
  isCustomProduct,
  isNewProduct,
  productId,
  quantity,
  initialStock,
  trackingType,
  startDate,
  endDate,
  locationId,
  orderId,
  name,
  fetchedProduct,
}: UseProductItemAvailabilityProps): UseProductItemAvailabilityReturn => {
  const { getAvailableCount } = useAPI();

  const [qtyAvailable, setQtyAvailable] = useState<number | undefined>(isRental ? undefined : initialStock.saleStock);
  const [showQtyWarning, setShowQtyWarning] = useState<boolean>(false);
  const [showOverbookWarning, setShowOverbookWarning] = useState<boolean>(false);

  const checkAvailableQuantity = useCallback(
    (newQty: number, schedule?: { start: DateTime; end?: DateTime }) => {
      if (trackingType === undefined || trackingType === ProductTrackingType.NonInventory || isCustomProduct) {
        return;
      }

      const from = schedule?.start || startDate;
      const to = schedule?.end || endDate || (from ? from.plus({ days: 28 }) : undefined);

      if (!from || !to || !productId) {
        return;
      }

      getAvailableCount(
        productId,
        locationId,
        isRental ? OrderType.Rental : OrderType.Sale,
        from,
        to,
        orderId ? [orderId] : undefined,
      ).then((av) => {
        const isTrackedBySerial = trackingType === ProductTrackingType.SerialUnits;

        // Set available quantity for tracked by quantity (this number is visible in the UI)
        if (!isTrackedBySerial) {
          setQtyAvailable(av);
        }

        // Display warning if the available quantity is less than the new quantity (the warning depends on the tracking type)
        if (av < newQty) {
          // Show the correct warning based on the tracking type
          if (isTrackedBySerial) {
            setShowOverbookWarning(true);
          } else {
            setShowQtyWarning(true);
            toast(`Warning: You are exceeding the available quantity${name ? ` of ${name}` : ''}.`, 'warning');
          }
        }

        // Hide the warning if the available quantity is greater than or equal to the new quantity
        if (av >= newQty) {
          setShowQtyWarning(false);
          setShowOverbookWarning(false);
        }
      });
    },
    [trackingType, isRental, isCustomProduct, productId, startDate, endDate, locationId, orderId, name],
  );

  // Effect to trigger availability checking when product is fetched for new products
  useEffect(() => {
    if (fetchedProduct && isNewProduct && trackingType) {
      // Trigger availability check when product data is fetched
      checkAvailableQuantity(quantity);
    }
  }, [fetchedProduct, quantity, isNewProduct, locationId, checkAvailableQuantity]);

  // Effect to trigger availability checking for existing products on mount
  useEffect(() => {
    if (!isNewProduct && trackingType) {
      // Trigger availability check for existing products
      checkAvailableQuantity(quantity);
    }
  }, [quantity, isNewProduct, trackingType, checkAvailableQuantity]);

  return {
    qtyAvailable,
    showQtyWarning,
    showOverbookWarning,
    checkAvailableQuantity,
  };
};
